import { isValidElement, ReactNode } from 'react';

export function addMinutes(time, minsToAdd) {
  function D(J) {
    return (J < 10 ? '0' : '') + J;
  }
  const piece = time.split(':');
  const mins = piece[0] * 60 + +piece[1] + +minsToAdd;

  // eslint-disable-next-line no-bitwise
  return `${D(((mins % (24 * 60)) / 60) | 0)}:${D(mins % 60)}`;
}

export const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const { userAgent } = navigator;
  return (
    Boolean(userAgent.match(/Android/i)) ||
    Boolean(userAgent.match(/iPhone|iPad|iPod/i)) ||
    Boolean(userAgent.match(/Opera Mini/i)) ||
    Boolean(userAgent.match(/IEMobile/i))
  );
};

export function isIOS() {
  if (typeof navigator !== 'undefined' && typeof window === 'undefined') {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  }
  return false;
}

export function isAndroid() {
  if (typeof navigator !== 'undefined') {
    return /android/i.test(navigator.userAgent);
  }
  return false;
}

export function luminance(hex) {
  const c = hex.replace('#', '');
  const rgb = parseInt(c, 16); // convert rrggbb to decimal
  const r = (rgb >> 16) & 0xff; // eslint-disable-line no-bitwise
  const g = (rgb >> 8) & 0xff; // eslint-disable-line no-bitwise
  const b = (rgb >> 0) & 0xff; // eslint-disable-line no-bitwise

  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709

  return luma;
}

export async function sha256(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);

  // convert ArrayBuffer to Array
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  // convert bytes to hex string
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

// Gets the inner string of a React Node, only works if the text is among the first children
export function getInnerText(reactNode: ReactNode) {
  if (isValidElement(reactNode)) {
    const { children } = reactNode.props;

    if (typeof children === 'string') {
      return children;
    }

    if (Array.isArray(children)) {
      return children.filter(child => typeof child === 'string').join('');
    }
  }

  return '';
}

export function sanitizeNumberHeader(header: number | string | string[] | undefined): number {
  if (Array.isArray(header)) {
    return parseFloat(header[0]) || NaN;
  }
  if (typeof header === 'number') {
    return header;
  }
  return parseFloat(header || '') || NaN;
}

export function sanitizeStringHeader(header: string | string[] | undefined): string {
  if (Array.isArray(header)) {
    return header[0] || '';
  }
  return header || '';
}

interface Coordinates {
  latitude: number;
  longitude: number;
}

const toRadians = (degrees: number): number => degrees * (Math.PI / 180);

const haversineDistance = (coords1: Coordinates, coords2: Coordinates): number => {
  const R = 3958.8; // Radius of the Earth in miles
  const lat1 = toRadians(coords1.latitude);
  const lat2 = toRadians(coords2.latitude);
  const dLat = toRadians(coords2.latitude - coords1.latitude);
  const dLon = toRadians(coords2.longitude - coords1.longitude);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export function filterStoresWithinDistance(
  userCoords: Coordinates,
  stores: any[],
  maxDistance: number,
  searchInput: string = ''
) {
  // check US postcode format
  const isZipInput = /^\d{5}$/.test(searchInput.trim());
  const targetZip = searchInput.trim();

  return stores
    .filter(store => {
      const storeCoords: Coordinates = {
        latitude: parseFloat(store.latitude),
        longitude: parseFloat(store.longitude),
      };

      // Check if coordinates are valid numbers
      if (Number.isNaN(storeCoords.latitude) || Number.isNaN(storeCoords.longitude)) {
        return false;
      }

      const dist = haversineDistance(userCoords, storeCoords);

      return dist <= maxDistance;
    })
    .sort((a, b) => {
      if (isZipInput) {
        const zipA = (a.zipCode || a.postalCode || '').toString();
        const zipB = (b.zipCode || b.postalCode || '').toString();

        const isAMatch = zipA === targetZip;
        const isBMatch = zipB === targetZip;

        if (isAMatch && !isBMatch) return -1;
        if (!isAMatch && isBMatch) return 1;
      }
      const storeCoordsA: Coordinates = {
        latitude: parseFloat(a.latitude),
        longitude: parseFloat(a.longitude),
      };
      const storeCoordsB: Coordinates = {
        latitude: parseFloat(b.latitude),
        longitude: parseFloat(b.longitude),
      };

      const distanceA = haversineDistance(userCoords, storeCoordsA);
      const distanceB = haversineDistance(userCoords, storeCoordsB);

      return distanceA - distanceB;
    });
}

export function getBrowserInfo() {
  let OperatingSystem = 'Unknown';
  let Device = '';
  let Browser = '';
  if (typeof navigator === 'undefined') return { OperatingSystem, Device, Browser };

  const navApp = navigator.userAgent.toLowerCase();
  if (navApp.indexOf('win') !== -1) {
    OperatingSystem = 'Windows';
  } else if (navApp.indexOf('mac') !== -1) {
    OperatingSystem = 'MacOs';
  } else if (navApp.indexOf('linux') !== -1) {
    OperatingSystem = 'Linux';
  } else if (navApp.indexOf('x11') !== -1) {
    OperatingSystem = 'Unix';
  }

  if (/(tablet|ipad|playbook)|(android(?!.*mobi))/i.test(navigator.userAgent)) {
    Device = 'Tablet';
  } else if (
    /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera Mobi/.test(
      navigator.userAgent
    )
  ) {
    Device = 'Mobile';
  } else {
    Device = 'Desktop';
  }

  if (navigator.userAgent.indexOf('Chrome') > -1) {
    Browser = 'Google Chrome';
  } else if (navigator.userAgent.indexOf('Firefox') > -1) {
    Browser = 'Mozilla Firefox';
  } else if (navigator.userAgent.indexOf('Safari') > -1 && !navigator.userAgent.includes('Chrome')) {
    Browser = 'Apple Safari';
  } else if (navigator.userAgent.indexOf('Edge') > -1) {
    Browser = 'Microsoft Edge';
  } else if (navigator.userAgent.indexOf('Trident') > -1 || navigator.userAgent.indexOf('MSIE') > -1) {
    Browser = 'Internet Explorer';
  } else if (navigator.userAgent.indexOf('OPR') > -1) {
    Browser = 'Opera';
  } else {
    Browser = 'Unknown Browser';
  }
  return { OperatingSystem, Device, Browser };
}

// For now I leave this code here
const STORAGE_KEY = 'chat_origin';

export const saveChatOriginToStorage = (origin: string) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, origin);
  }
};

export const getChatOriginFromStorage = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(STORAGE_KEY);
  }
  return null;
};

export const removeChatOriginFromStorage = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(STORAGE_KEY);
  }
};

// Returns date in the format YYYY-MM-DD
export function getFormattedDate(date: Date | null) {
  const dateToFormat = date || new Date();
  const formattedDate = new Date(
    new Date(dateToFormat).getTime() - new Date(dateToFormat).getTimezoneOffset() * 60 * 1000
  );

  return formattedDate.toISOString().split('T')[0];
}

// Returns a Date object from a date string in the format YYYY-MM-DD
export function getOriginalDate(formattedDate: string): Date {
  const dateWithoutTimezoneOffset = new Date(formattedDate);
  const timezoneOffset = dateWithoutTimezoneOffset.getTimezoneOffset() * 60 * 1000;
  const originalDate = new Date(dateWithoutTimezoneOffset.getTime() + timezoneOffset);

  return originalDate;
}

/* eslint-disable no-bitwise */
function utf8Encode(argString: string | null | undefined): string {
  if (argString === null || typeof argString === 'undefined') {
    return '';
  }
  const string = `${argString}`;
  let utftext = '';
  let start = 0;
  let end = 0;
  const stringl = string.length;

  for (let n = 0; n < stringl; n += 1) {
    const c1 = string.charCodeAt(n);
    let enc: string | null = null;

    if (c1 < 128) {
      end += 1;
    } else if (c1 > 127 && c1 < 2048) {
      enc = String.fromCharCode((c1 >> 6) | 192, (c1 & 63) | 128);
    } else if ((c1 & 0xf800) !== 0xd800) {
      enc = String.fromCharCode((c1 >> 12) | 224, ((c1 >> 6) & 63) | 128, (c1 & 63) | 128);
    } else {
      // surrogate pairs
      if ((c1 & 0xfc00) !== 0xd800) {
        throw new RangeError(`Unmatched trail surrogate at ${n}`);
      }
      const c2 = string.charCodeAt((n += 1));
      if ((c2 & 0xfc00) !== 0xdc00) {
        throw new RangeError(`Unmatched lead surrogate at ${n - 1}`);
      }
      const c = ((c1 & 0x3ff) << 10) + (c2 & 0x3ff) + 0x10000;
      enc = String.fromCharCode((c >> 18) | 240, ((c >> 12) & 63) | 128, ((c >> 6) & 63) | 128, (c & 63) | 128);
    }

    if (enc !== null) {
      if (end > start) {
        utftext += string.slice(start, end);
      }
      utftext += enc;
      start = n + 1;
      end = n + 1;
    }
  }

  if (end > start) {
    utftext += string.slice(start, stringl);
  }

  return utftext;
}
/* eslint-enable no-bitwise */

async function digestMessage(message: string): Promise<string> {
  const msgUint8 = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

export async function hashMessage(message: string): Promise<string> {
  // UTF-8 encode the message and then hash it
  const utf8EncodedMessage = utf8Encode(message);
  const hash = await digestMessage(utf8EncodedMessage);
  return hash;
}

export type DeviceType = 'mobile' | 'tablet' | 'desktop';

export const moreDescriptiveAriaLabel = (title: string | undefined, label: string): string => {
  if (
    label.toLowerCase().trim() === 'learn more' ||
    label.toLowerCase().trim() === 'read more' ||
    label.toLowerCase().trim() === 'get started' ||
    label.toLowerCase().trim() === 'shop now' ||
    label.toLowerCase().trim() === 'shop the collection'
  ) {
    return `${title ?? ''}${!!title && ' - '}${label}`;
  }

  return label;
};

export function isValidGeocodeInput(input: string): boolean {
  const query = input.trim();

  // 1. block single char ( "9", "L", "Y", "/", "*")
  if (!query || query.length < 2) return false;

  // 2. block punctuation ("?!@")
  const punctuationRegex = /^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/;
  if (punctuationRegex.test(query)) return false;

  // 3. postcode
  const isPureNumber = /^\d+$/.test(query);
  if (isPureNumber) {
    return query.length === 4 || query.length === 5;
  }

  const hasLetters = /[a-zA-Z]/.test(query);
  if (hasLetters) {
    return true;
  }

  return false;
}

export const isJaneBirthday = () => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    month: 'numeric',
    day: 'numeric',
  });

  const dateStr = formatter.format(new Date());
  return dateStr === '4/3';
};
